import { language_t } from '@/store/modules/pref'
import { EdidMode, ApiVwTx, ApiVwRx, RotateFlip, Extender, Matrix, extender_series_t } from '@/types/api'
import i18n from '@/i18n'
import { IDevice, IVW } from '@/types/model'
import { $s, State as RootState } from '@/store'
import { fromEvent, interval } from 'rxjs'
import { debounce } from 'rxjs/operators'
import { MatrixMap, getDev } from '@/store/modules/device'
import VueI18n from 'vue-i18n'

export const $COLOR = {
  PRIMARY: '#d6314b'
}
export const convertToDataUrl = (blob: any): Promise<any> => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onloadend = () => resolve(reader.result)
  reader.onerror = reject
  reader.readAsDataURL(blob)
})
export function deepFreeze (object: object): object {
  // Retrieve the property names defined on object
  const propNames = Object.getOwnPropertyNames(object)
  // Freeze properties before freezing self
  for (const name of propNames) {
    const value = object[name]
    object[name] = value && typeof value === 'object' ? deepFreeze(value) : value
  }
  return Object.freeze(object)
}

/** Don't use this in a recursively-referenced object */
export function deepCopy <T> (x: T): T {
  try {
    return JSON.parse(JSON.stringify(x))
  } catch (err) {
    console.error('[utils] deepCopy() cannot accept an invalid JS data structure.', err)
    throw err
  }
}

/** Return a array without any duplicated item */
export function unique <T> (arr: T[]): T[] {
  const fin: T[] = []
  for (const x of arr) {
    if (fin.includes(x)) { continue }
    fin.push(x)
  }
  return fin
}

export function generateUnoccupiedId <T extends {id: number}> (arr: T[]): number {
  const occupied = arr.map(vw => vw.id)
  for (let i = 1; i < 65535; i++) {
    if (!occupied.includes(i)) {
      return i
    }
  }
  throw new Error('[Is this possible?] Too many VideoWall...')
}

export function checkNameDuplicated <T extends {name: string}> (newName: string, oldName: string | null, list: T[]): boolean {
  return list.some(x => x.name !== oldName && x.name === newName)
}

export function checkDeviceNameDuplicated <T extends {deviceName: string}> (newName: string, oldName: string | null, list: T[]): boolean {
  return list.filter(x => x.deviceName !== '').some(x => x.deviceName !== oldName && x.deviceName === newName)
}

export function SORT_BY_MAC_FN <T extends {mac: string}> (a: T, b: T): number {
  return a.mac > b.mac ? 1 : -1
}

export function SORT_BY_ID_FN <T extends {id: number}> (a: T, b: T): number {
  return b.id - a.id
}

export function SORT_DEVICE_GENERAL_FN (a: Extender, b: Extender): number {
  const aOn = a.connectionTime > 0
  const bOn = b.connectionTime > 0
  if (aOn !== bOn) { return ~~bOn - ~~aOn }
  if (a.vendor !== b.vendor) {return a.vendor !== 'Aspeed' ? 1 : -1 }
  if (a.deviceType !== b.deviceType) { return a.deviceType === 'TX' ? 1 : -1 }
  return a.deviceName.localeCompare(b.deviceName)
}

export function SORT_BY_NAME_FN <T extends {name: string}> (a: T, b: T): number {
  return b.name.toLowerCase() < a.name.toLowerCase() ? 1 : -1
}


export const ppp = (...args: any[]): void => {
  // this.$options['__filename']
  console.log(...args.map((x) => typeof x === 'object' ? deepCopy(x) : x))
}

export function sleep (milliseconds: number): Promise<any> {
  return new Promise((resolve, reject) => {
    window.setTimeout(resolve, milliseconds)
  })
}

export function diff <T> (aa: T[], bb: T[]): T[] {
  const fin: T[] = []
  for (const a of aa) {
    if (!bb.includes(a)) {
      fin.push(a)
    }
  }
  for (const b of bb) {
    if (!aa.includes(b)) {
      fin.push(b)
    }
  }
  return fin
}

export function devIsOnline (dev: IDevice | string): boolean {
  const d = getDev(dev)
  if (!d) {return false}
  return d.connectionTime > 0
}
export function getDevSeries (devOrVendor: IDevice | string): extender_series_t {
  const vendor = typeof devOrVendor === 'string' ? devOrVendor : devOrVendor.vendor
  return vendor === 'Aspeed' ? 'HG' : 'HX'
}
// ==========================================
// Getter (Vuex's getter is suck and buggy)
// ==========================================

/**
 * Get { [mac: string]: IDevice[] } according groupId.
 * (Vuex's getter is suck and buggy so use such method)
 * NOTE: NOT include the RXs which are not being used in Matrix (That is rxInfo.activeState.which must be 'matrix')
 */
export function getMatrixMap (state: RootState): MatrixMap {
  const fin: MatrixMap = new Map<string, IDevice[]>()
  for (const dev of state.device.list) {
    if (dev.deviceType === 'RX' && dev.rxInfo.activeState.which !== 'matrix') {
      continue
    }
    const oppositeType = dev.deviceType === 'TX' ? 'RX' : 'TX'
    const sameGroupDevs = state.device.list.filter((x) => x.groupID === dev.groupID)
    let oppositeDevs: IDevice[] = []
    if (oppositeType === 'RX') {
      oppositeDevs = sameGroupDevs.filter((x) => x.deviceType === 'RX' && x.rxInfo.activeState.which === 'matrix')
    } else {
      oppositeDevs = sameGroupDevs.filter((x) => x.deviceType === 'TX')
    }
    if (oppositeDevs.length === 0) {
      fin.set(dev.mac, [])
    } else if (oppositeType === 'TX') {
      if (oppositeDevs.length > 1) { console.warn('[For Developer] Multiple TX has same groupID? You should validate to avoid such configuration.') }
      fin.set(dev.mac, oppositeDevs)
    } else {
      fin.set(dev.mac, oppositeDevs)
    }
  }
  return fin
}

export function getMatrixList (state: RootState): Matrix[] {
  return state.device.list
  .filter(x => x.deviceType === 'RX' && x.rxInfo.activeState.which === 'matrix')
  .map(rx => Object.assign(rx.rxInfo.activeState.matrix))
}

/** A TX is being used in...  */
interface TxUsage {
  matrixRxs: IDevice[]
  vwSettings: number[]
}

/** A RX is being used as...  */
interface RxUsage {
  matrixTx?: IDevice
  vwTx?: number
}

export interface ExtenderActiveStateSummary {
  matrix?: VueI18n.TranslateResult
  vw?: VueI18n.TranslateResult
  unused?: string
}

/** VW: only include the rxInfo.activeState.which === vw */
export function getExtenderActiveUsage (state: RootState, extender: IDevice): ExtenderActiveStateSummary {
  const allDev = state.device.list
  const UNUSED: ExtenderActiveStateSummary = { unused: `(${i18n.t('term.unused')})` }
  if (extender.deviceType === 'TX') {
    // NOTE: only connectionTime > 0 ?
    const rxList = allDev.filter(x => x.deviceType === 'RX' && x.groupID === extender.groupID && x.connectionTime >= 0)
    const asMatrixCount = rxList.filter(rx => rx.rxInfo.activeState.which === 'matrix').length
    const asVwCount = rxList.filter(rx => rx.rxInfo.activeState.which === 'vw').length
    if (extender.deviceName.includes('藍')) { console.log('============>', deepCopy(rxList)) }
    const fin: ExtenderActiveStateSummary = { }
    if (asMatrixCount === 0 && asVwCount === 0) { return UNUSED }
    if (asMatrixCount > 0) { fin.matrix = i18n.tc('call.tx.matrixTo', asMatrixCount, [asMatrixCount]) }
    if (asVwCount > 0) { fin.vw = i18n.tc('call.tx.matrixTo', asVwCount, [asVwCount]) }
    return fin
  } else {  // RX
    const sourceTx = allDev.find(x => x.deviceType === 'TX' && x.groupID === extender.groupID)
    if (!sourceTx) { return {} }
    const usedAs = extender.rxInfo.activeState.which
    if (usedAs === 'matrix') { return {matrix: i18n.t('call.rx.matrixFrom', [sourceTx.deviceName])} }
    if (usedAs === 'vw') {
      const vw = state.device.vwList.find(v => v.id === extender.rxInfo.activeState.vwid)  // FIXME: This should get from cached VW instead VW settings
      const vwName = vw ? vw.name : `(${i18n.t('term.deleted')})`
      return { vw: i18n.t('call.rx.inVw', [vwName, sourceTx.deviceName]) }
    }
    return UNUSED
  }
}

// ==========================================
// Watch window resize
// ==========================================
type UIEventHandler = (ev: UIEvent) => any
type Canceller = () => void
const WIN_RESIZE_CB: UIEventHandler[] = []

window.addEventListener('resize', (resizeEv) => {
  for (const fn of WIN_RESIZE_CB) {
    fn(resizeEv)
  }
})

export function whenWindowResized (cb: UIEventHandler): Canceller {
  WIN_RESIZE_CB.push(cb)
  return () => {
    const idx = WIN_RESIZE_CB.indexOf(cb)
    WIN_RESIZE_CB.splice(idx, 1)
  }
}

// Handle window resize
const obWinResize = fromEvent(window, 'resize')
const obDebouncedWinResize = obWinResize.pipe(debounce(() => interval(300)))
obDebouncedWinResize.subscribe(x => {
  if (!x.target) { return }
  const target = x.target as Window
  $s.common.mutations.assignWindowSize({width: target.innerWidth, height: target.innerHeight})
})
function generateFakeMac (): string {
  return Array(6).fill(0).map(() => {
    let seg = (Math.floor(Math.random() * 1000000) % 257).toString(16)
    if (seg.length <= 1) { seg = '0' + seg }
    return seg.toUpperCase()
  }).join(':')
}
export function generateFakeDevs (txAmount: number, rxAmount: number): Extender[] {
  const fin: Extender[] = []
  const tmp: Extender = {
    deviceName: '[[[Fake TX]]]',
    mac: '82:FE:80:87:4F:FF',
    ip: '333.333.17.233',
    netmask: '255.255.255.0',
    gateway: '333.333.16.254',
    deviceType: 'TX',
    vendor: 'Aspeed',
    groupID: 1835,
    uptime: -1,
    connectionTime: -1,
    dhcpStatus: false,
    disableAVOutput: true,
    rxScreenMode: 0,
    ledBlinking: false,
    cecFlag: false,
    osdResolution: '640x480@60',
    txInfo: {
      edidMode: 0,
      learningEDIDFrom: '',
      learningType: 'dynamic',
      sourceDetected: false,
    },
    rxInfo: {
      screenMode: '',
      tvDetected: false,
      activeState: {
        which: 'matrix',
        vwid: -1,
        matrix: {
          osdid: 0,
          rotateFlip: RotateFlip.normal,
          rxMac: '',
          txMac: '',
        }
      },
      vwTxWindow: {
        x1: 0,
        x2: 0,
        y1: 0,
        y2: 0,
      }
    },
    multicastAddress: '',
    upScaleMode: 'passThrough',
    hdcpStatus: false,
    modelName: 'FAKE MODEL',
    sn: '12345678901234567890'
  }
  for (let i = 0; i < txAmount; i++) {
    const mac = generateFakeMac()
    fin.push(Object.assign(deepCopy(tmp), {
      deviceName: `[[[Dummy TX Hello World ${mac.slice(12)}]]]`,
      mac: mac,
      deviceType: 'TX'
    }))
  }
  for (let i = 0; i < txAmount; i++) {
    const mac = generateFakeMac()
    fin.push(Object.assign(deepCopy(tmp), {
      deviceName: `[[[Dummy RX Hello World ${mac.slice(12)}]]]`,
      mac: mac,
      deviceType: 'RX'
    }))
  }
  return fin
}

/** Intriglyph's CSS class */
export function rotateFlipToIgClass (x: RotateFlip): string {
  switch (x) {
    case RotateFlip.normal: return ''
    case RotateFlip.vert: return 'ig-flip-vertical'
    case RotateFlip.hori: return 'ig-flip-horizontal'
    case RotateFlip.rotate180: return 'ig-rotate-180'
    case RotateFlip.rotate90_hori: return 'ig-rotate-90 ig-flip-horizontal'
    case RotateFlip.rotate90: return 'ig-rotate-90'
    case RotateFlip.rotate270: return 'ig-rotate-270'
    case RotateFlip.rotate270_hori: return 'ig-rotate-270 ig-flip-horizontal'
  }
}

/** Convert magic code into i18n translated string */
export class magicCode2Text {
  /** simply convert enum EdidMode to string, NOT include learningEDID */
  static edidMode (x: EdidMode): string {
    switch (x) {
      case EdidMode.default:          return `4K2K@60Hz HDR-24bit 2D video & 2ch audio`
      case EdidMode._720P_LPCM:       return '720p LPCM'
      case EdidMode._1080P_dolby:     return '1080p Dolby'
      case EdidMode._1080P_LPCM:      return '1080p LPCM'
      case EdidMode._3840x2160_dolby: return '3840 x 2160 Dolby'
      case EdidMode._3840x2160_LPCM:  return '3840 x 2160 LPCM'
      case EdidMode._4096x2160_dolby: return '4096 x 2160 Dolby'
      case EdidMode.learningEDID: return '' as string
    }
    return i18n.t('term.unknown') as string
  }
  /** e.g.
   * Fixed (7080p Dolby)
   * Learning (from RX "Bravia_1")
   */
  static edidDesc (dev: IDevice): string {
    const isFixed = this.edidMode_isFixed(dev.txInfo.edidMode)
    console.log('dev edidMode ===', dev.txInfo.edidMode)
    if (isFixed) {
      return this.edidMethod(dev.txInfo.edidMode) + ' ( ' + this.edidMode(dev.txInfo.edidMode) + ' ) '
    } else {
      const learnedFromDev = $s.$state.device.list.find((d) => d.mac === dev.txInfo.learningEDIDFrom)
      const learnedFromDevName = learnedFromDev ? learnedFromDev.deviceName : ''
      if (learnedFromDevName === '') {
        return i18n.t('term.invalidSetting') as string  // TODO: Make this DRY, show warning icon
      } else {
        return this.edidMethod(dev.txInfo.edidMode) + i18n.t('edidMode.learning_desc', [learnedFromDevName])
      }
    }
  }
  /** Abstract concept.
   * @return {string} Fixed | Learning
   */
  static edidMethod (x: EdidMode): string {
    if (this.edidMode_isFixed(x)) { return i18n.t('edidMode.fixed') as string }
    return i18n.t('edidMode.learning') as string
  }
  static yesNo (x: boolean): string {
    return (x ? i18n.t('term.yes') : i18n.t('term.no')) as string
  }
  static detected (x: boolean): string {
    return (x ? i18n.t('term.detected') : i18n.t('term.undetected')) as string
  }
  static langNativeName (x: language_t): string {
    switch (x) {
      case 'auto': return i18n.t('sys.lang.auto') as string
      case 'en':
      case 'zh':
      case 'ja':
      return i18n.t('langName', x) as string
      default: return '[To Developer] Invalid language'
    }
  }
  static rotateFlip (x: RotateFlip): string {
    switch (x) {
      case RotateFlip.normal: return 'Normal'
      case RotateFlip.vert: return 'Flip vertical'
      case RotateFlip.hori: return 'Flip horizontal'
      case RotateFlip.rotate180: return 'Rotate 180'
      case RotateFlip.rotate90_hori: return 'Rotate 90 + Flip horizontal'
      case RotateFlip.rotate90: return 'Rotate 90'
      case RotateFlip.rotate270: return 'Rotate 270'
      case RotateFlip.rotate270_hori: return 'Rotate 270 + Flip horizontal'
    }
  }
  private static edidMode_isFixed (x: EdidMode): boolean {
    return x !== EdidMode.learningEDID
  }
}

/** DEPRECATED: not used currently. */
// https://stackoverflow.com/questions/12381563/how-to-stop-browser-back-button-using-javascript
export function preventBack (cb: () => void): () => void {
  const HASH = '!'
  const init = function () {
    window.location.href += '#'
    // making sure we have the fruit available for juice....
    // 50 milliseconds for just once do not cost much (^__^)
    window.setTimeout(function () {
      window.location.href += '!'
    }, 50)
  }
// Earlier we had setInerval here....
  window.onhashchange = function () {
    if (window.location.hash !== HASH) {
      window.location.hash = HASH
    }
  }
  window.onload = function () {
    init()
  }
  return function () {
    window.onhashchange = null
    window.onload = null
  }
}
